import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import ApiService from '../../../../services/ApiService'
import { PurchaseDemand } from './types'
import {
  CloseButton,
  ErrorText,
  FieldInput,
  FieldLabel,
  FieldSelect,
  FormRow,
  Overlay,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PrimaryButton,
  SecondaryButton,
} from '../ModalStyle'

interface Company {
  id: number
  companyName: string
}

interface EditDemandModalProps {
  demand: PurchaseDemand
  onClose: () => void
  onSaved: () => void
}

const EditDemandModal: React.FC<EditDemandModalProps> = ({ demand, onClose, onSaved }) => {
  const [companies, setCompanies] = useState<Company[]>([])
  const [supplierCompanyId, setSupplierCompanyId] = useState<string>(
    demand.supplierCompanyId != null ? String(demand.supplierCompanyId) : '',
  )
  const [requiredQuantity, setRequiredQuantity] = useState<string>(String(demand.requiredQuantity ?? ''))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    ApiService.companies
      .findAll({ take: 200 })
      .then((res: any) => setCompanies(res.data?.data ?? res.data ?? []))
      .catch(() => setCompanies([]))
  }, [])

  const isValid = requiredQuantity !== '' && Number(requiredQuantity) >= 0

  const handleSubmit = async () => {
    if (!isValid || saving) return
    setSaving(true)
    setError(null)
    try {
      await ApiService.purchaseDemand.update(demand.id, {
        supplierCompanyId: supplierCompanyId ? Number(supplierCompanyId) : undefined,
        requiredQuantity: Number(requiredQuantity),
      })
      onSaved()
      onClose()
    } catch (err) {
      console.error('Erro ao editar demanda de compra', err)
      setError('Não foi possível salvar a demanda de compra. Verifique os dados e tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Overlay onClick={onClose}>
      <Panel $width='30rem' onClick={(e) => e.stopPropagation()}>
        <PanelHeader>
          <h3>Editar Demanda de Compra</h3>
          <CloseButton onClick={onClose}>
            <X size={18} />
          </CloseButton>
        </PanelHeader>
        <PanelBody>
          <FormRow>
            <div>
              <FieldLabel>Código da DC</FieldLabel>
              <FieldInput value={demand.code} readOnly disabled />
            </div>
            <div>
              <FieldLabel>Tipo</FieldLabel>
              <FieldInput
                value={demand.PurchaseMaterial.type === 'MATERIA_PRIMA' ? 'Matéria Prima' : 'Insumo'}
                readOnly
                disabled
              />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Material</FieldLabel>
              <FieldInput value={demand.PurchaseMaterial.name} readOnly disabled />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Fornecedor</FieldLabel>
              <FieldSelect value={supplierCompanyId} onChange={(e) => setSupplierCompanyId(e.target.value)}>
                <option value=''>Selecione</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.companyName}
                  </option>
                ))}
              </FieldSelect>
            </div>
            <div>
              <FieldLabel>Quantidade necessária*</FieldLabel>
              <FieldInput
                type='number'
                value={requiredQuantity}
                onChange={(e) => setRequiredQuantity(e.target.value)}
              />
            </div>
          </FormRow>
          {error && <ErrorText>{error}</ErrorText>}
        </PanelBody>
        <PanelFooter>
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton disabled={!isValid || saving} onClick={handleSubmit}>
            {saving ? 'Salvando...' : 'Salvar'}
          </PrimaryButton>
        </PanelFooter>
      </Panel>
    </Overlay>
  )
}

export default EditDemandModal
